import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

const MAX_DISCOUNT_CODE_LEN = 18;

const inputClass =
  "w-full px-3 py-2 border-2 border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark";
const passwordInputClass =
  "w-full pl-3 pr-10 py-2 border-2 border-rule rounded-sm bg-newsprint-panel text-ink focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark";
const labelClass = "block text-sm font-medium text-ink mb-1";
const primaryButtonClass =
  "w-full py-2 px-4 bg-ink text-newsprint font-medium rounded-sm hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed";

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { idToken, signIn, register, error, clearError, configReady } = useAuth();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);

  useEffect(() => {
    if (idToken) navigate("/app", { replace: true });
  }, [idToken, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLoading(true);
    try {
      await signIn(email, password, rememberMe);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const code = discountCode.trim().slice(0, MAX_DISCOUNT_CODE_LEN);
    if (!code) {
      return;
    }
    setLoading(true);
    try {
      await register(email, password, code);
    } finally {
      setLoading(false);
    }
  };

  if (!configReady) {
    return (
      <div className="min-h-screen bg-newsprint text-ink flex items-center justify-center">
        <p className="text-ink/80">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-newsprint text-ink flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-newsprint-panel border border-rule rounded-sm p-8">
        <h1 className="text-2xl font-semibold font-headline mb-6 text-center text-ink">
          CountingTrades.com
        </h1>

        {error && (
          <div
            className="mb-4 p-3 rounded-sm bg-table-loss border border-error text-error"
            role="alert"
          >
            <p className="text-sm">{error}</p>
          </div>
        )}

        {mode === "signin" ? (
          <>
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label htmlFor="login-email" className={labelClass}>
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={inputClass}
                />
              </div>
              <div className="relative">
                <label htmlFor="login-password" className={labelClass}>
                  Password
                </label>
                <input
                  id="login-password"
                  type={showLoginPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={passwordInputClass}
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPassword((v) => !v)}
                  aria-label={showLoginPassword ? "Hide password" : "Show password"}
                  className="absolute right-0 top-9 bottom-0 flex items-center justify-center w-10 text-ink/70 hover:text-ink hover:bg-newsprint-dark rounded-r-sm border-l border-rule"
                >
                  {showLoginPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <div>
                <span className="block text-sm font-medium text-ink mb-2">When I sign in:</span>
                <div
                  role="radiogroup"
                  aria-label="Keep me logged in or this session only"
                  className="inline-flex rounded-sm border border-rule bg-newsprint-dark p-0.5"
                >
                  {(
                    [
                      { value: true, label: "Keep me logged in" },
                      { value: false, label: "This session only" },
                    ] as const
                  ).map(({ value, label }) => (
                    <button
                      key={String(value)}
                      type="button"
                      role="radio"
                      aria-checked={rememberMe === value}
                      onClick={() => setRememberMe(value)}
                      className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                        rememberMe === value ? "bg-ink text-newsprint" : "text-ink hover:bg-newsprint"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={loading} className={primaryButtonClass}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <p className="mt-4 text-sm text-center text-ink/80">
              No account?{" "}
              <button
                type="button"
                onClick={() => {
                  setMode("signup");
                  clearError();
                }}
                className="underline hover:no-underline text-ink"
              >
                Create account
              </button>
            </p>
          </>
        ) : (
          <>
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label htmlFor="reg-email" className={labelClass}>
                  Email
                </label>
                <input
                  id="reg-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={inputClass}
                />
              </div>
              <div className="relative">
                <label htmlFor="reg-password" className={labelClass}>
                  Password
                </label>
                <input
                  id="reg-password"
                  type={showRegPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className={passwordInputClass}
                />
                <button
                  type="button"
                  onClick={() => setShowRegPassword((v) => !v)}
                  aria-label={showRegPassword ? "Hide password" : "Show password"}
                  className="absolute right-0 top-9 bottom-0 flex items-center justify-center w-10 text-ink/70 hover:text-ink hover:bg-newsprint-dark rounded-r-sm border-l border-rule"
                >
                  {showRegPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <div>
                <label htmlFor="reg-discount" className={labelClass}>
                  Beta Invite Code (required, max {MAX_DISCOUNT_CODE_LEN} characters)
                </label>
                <input
                  id="reg-discount"
                  type="text"
                  maxLength={MAX_DISCOUNT_CODE_LEN}
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.slice(0, MAX_DISCOUNT_CODE_LEN))}
                  required
                  className={inputClass}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !discountCode.trim()}
                className={primaryButtonClass}
              >
                {loading ? "Creating account…" : "Create account"}
              </button>
            </form>
            <p className="mt-4 text-sm text-center text-ink/80">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  setMode("signin");
                  clearError();
                }}
                className="underline hover:no-underline text-ink"
              >
                Sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
